import { findElement } from './dom.js';
let audioCtx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let sourceNode: MediaElementAudioSourceNode | null = null;
let animationFrameId: number | null = null;
let isInitialized = false;

const NUM_BARS = 16;

export function initVisualizer() {
  const container = findElement('audio-visualizer', HTMLElement);
  const audioPlayer = findElement('audio-player', HTMLAudioElement);
  if (!container || !audioPlayer) return;

  // Set crossorigin attribute for CORS audio access
  audioPlayer.setAttribute('crossorigin', 'anonymous');

  // Render 16 spectrum bars
  container.innerHTML = '';
  const bars: HTMLDivElement[] = [];
  for (let i = 0; i < NUM_BARS; i++) {
    const bar = document.createElement('div');
    bar.className = 'bar';
    container.appendChild(bar);
    bars.push(bar);
  }

  const setupWebAudio = () => {
    if (isInitialized) {
      if (audioCtx && audioCtx.state === 'suspended') {
        audioCtx.resume();
      }
      return;
    }

    try {
      const AudioContextClass = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (typeof AudioContextClass !== 'function') return;

      audioCtx = new AudioContextClass();
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64; // 32 frequency bins
      analyser.smoothingTimeConstant = 0.75;

      sourceNode = audioCtx.createMediaElementSource(audioPlayer);
      sourceNode.connect(analyser);
      analyser.connect(audioCtx.destination);

      isInitialized = true;
    } catch (e) {
      console.warn('Web Audio source connection note:', e);
    }
  };

  const startLoop = () => {
    setupWebAudio();
    if (audioCtx && audioCtx.state === 'suspended') {
      audioCtx.resume();
    }
    if (animationFrameId) return;

    const bufferLength = analyser ? analyser.frequencyBinCount : 32;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (audioPlayer.paused || audioPlayer.ended) {
        stopLoop();
        return;
      }

      animationFrameId = requestAnimationFrame(draw);

      if (analyser) {
        analyser.getByteFrequencyData(dataArray);
      }

      // Map real FFT frequency data to 16 spectrum bars
      for (let i = 0; i < NUM_BARS; i++) {
        const val = dataArray[i] || 0;
        const percent = Math.max(4, Math.min(100, (val / 255) * 100));
        const bar = bars[i];
        if (bar) {
          bar.style.height = `${percent.toFixed(1)}%`;
          bar.style.opacity = percent > 5 ? '1' : '0.35';
        }
      }
    };

    draw();
  };

  const stopLoop = () => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    for (let i = 0; i < bars.length; i++) {
      const bar = bars[i];
        if (bar) {
        bar.style.height = '4px';
        bar.style.opacity = '0.3';
      }
    }
  };

  audioPlayer.addEventListener('play', startLoop);
  audioPlayer.addEventListener('playing', startLoop);
  audioPlayer.addEventListener('pause', stopLoop);
  audioPlayer.addEventListener('ended', stopLoop);

  if (!audioPlayer.paused && audioPlayer.currentTime > 0) {
    startLoop();
  }
}
